import type { Constituent, EvidenceKind, ReferenceEvidence, ReferenceIndex, Resolution, Trim } from './types';
import { hyphenParts, joined, normalizeToken, PARTICLE_WORDS, POSSESSIVE_RE, stripParticles, words } from './normalize';
import { CONNECTOR_WORDS } from './find-spans';

/**
 * Resolve one cited work against the reference index on evidence alone.
 *
 * Scoring rule:
 *   1. A reference is OUT unless the first written name matches its first author, every further
 *      written name matches one of its authors, its year matches, and any suffix agrees.
 *   2. Surviving references score: first author +3 (+2 through a hyphen part or the raw line),
 *      each co-author +2 (+1 weak), exact year +2, matching suffix +2, author count consistent
 *      with the names written +1 (`et al.` needs at least two authors beyond them; without
 *      it the count must equal the number of names).
 *   3. The winner must be strictly ahead; a tie is `ambiguous` with the tied ids, no survivor is
 *      `unresolved`. A reference whose names fit but which has no year is never a survivor; it is
 *      named in `undated` of the unresolved result, so the miss reads "has no year", not "no such author".
 *   4. A year one off is tried only when no reference matched the exact year, only for a
 *      citation without a suffix, and only if exactly ONE reference then survives.
 *   5. List order never decides anything.
 *
 * Left trim: the span grammar captures capitalised runs, so prose can end up in front of the
 * names (`Curiously, Smith and Jones`, `Results of Smith`, `Berlin Smith`). When NO reference
 * accepts the first written name, the resolver retries with leading material removed, one level
 * at a time — first whole leading names, then leading words of the first name — and decides at
 * the first level that has any survivor (a strict winner resolves, a tie is ambiguous). Two
 * rules keep this from guessing: a name joined to the next by `and`/`&` is never split off, and
 * a word some reference claims as an author is never dropped. The result carries `namesFrom`
 * and `trimmedPrefix` so the caller can move the span off the dropped words.
 */

interface NameVariants {
  strong: string[];
  weak: string[];
}

function variantsOf(written: string): NameVariants {
  const depossessed = written.replace(POSSESSIVE_RE, '');
  // A possessive is compared only in its de-possessed form: the raw `Cohen's` folds to `cohens`,
  // which could match a different surname `Cohens`, never the intended `Cohen`.
  const forms = depossessed !== written ? [depossessed] : [written];
  const strong = new Set<string>();
  const weak = new Set<string>();
  for (const f of forms) {
    const ws = words(f);
    if (ws.length === 0) continue;
    strong.add(joined(ws));
    strong.add(joined(stripParticles(ws)));
    for (const w of ws) for (const p of hyphenParts(w)) weak.add(normalizeToken(p));
  }
  for (const s of strong) weak.delete(s);
  return { strong: [...strong].filter(Boolean), weak: [...weak].filter(Boolean) };
}

interface Match {
  form: string;
  kind: EvidenceKind;
  weak: boolean;
}

function bestMatch(v: NameVariants, evidence: Map<string, EvidenceKind>): Match | null {
  let best: Match | null = null;
  const consider = (form: string, weakVariant: boolean) => {
    const kind = evidence.get(form);
    if (!kind) return;
    const weak = weakVariant || kind === 'hyphen-part' || kind === 'raw-head';
    if (!best || (best.weak && !weak)) best = { form, kind, weak };
  };
  for (const f of v.strong) consider(f, false);
  for (const f of v.weak) consider(f, true);
  return best;
}

interface Scored {
  id: string;
  score: number;
  evidence: string[];
}

type Rejection = 'first-author' | 'co-author' | 'year' | 'suffix' | 'no-year';

function score(c: Constituent, ref: ReferenceEvidence, yearTolerance: 0 | 1): Scored | Rejection {
  const evidence: string[] = [];
  let total = 0;

  const [first, ...rest] = c.names;
  if (first) {
    const m = bestMatch(variantsOf(first), ref.firstAuthor);
    if (!m) return 'first-author';
    total += m.weak ? 2 : 3;
    evidence.push(`first-author:${m.kind}:${m.form}`);
    for (const name of rest) {
      const cm = bestMatch(variantsOf(name), ref.authors);
      if (!cm) return 'co-author';
      total += cm.weak ? 1 : 2;
      evidence.push(`co-author:${cm.kind}:${cm.form}`);
    }
  }

  // The names fit, so the reference is the candidate and only its date is missing. Judged after
  // the names so a reference by the written author is never reported as no such author.
  if (!ref.year) return 'no-year';
  const delta = Math.abs(Number(c.year) - Number(ref.year));
  if (delta === 0) {
    total += 2;
    evidence.push(`year:${ref.year}`);
  } else if (delta === 1 && yearTolerance === 1 && !c.yearSuffix) {
    evidence.push(`year:${ref.year}~${c.year}`);
  } else return 'year';

  if (c.yearSuffix && ref.yearSuffix) {
    if (c.yearSuffix !== ref.yearSuffix) return 'suffix';
    total += 2;
    evidence.push(`suffix:${ref.yearSuffix}`);
  }

  if (ref.authorCount > 0 && c.names.length > 0) {
    // `et al.` says "and others": at least two authors beyond the names written.
    const consistent = c.etAl ? ref.authorCount >= c.arity + 2 : ref.authorCount === c.arity;
    if (consistent) {
      total += 1;
      evidence.push(`authors:${ref.authorCount}`);
    }
  }
  return { id: ref.id, score: total, evidence };
}

function decide(scored: Scored[], reason: string): Resolution {
  const sorted = [...scored].sort((a, b) => b.score - a.score);
  if (sorted.length === 1 || sorted[0].score > sorted[1].score) {
    const w = sorted[0];
    return { kind: 'resolved', id: w.id, score: w.score, evidence: w.evidence };
  }
  const top = sorted[0].score;
  return { kind: 'ambiguous', candidates: sorted.filter((s) => s.score === top).map((s) => s.id), reason };
}

interface Attempt {
  resolution: Resolution;
  /** True when every reference was rejected on the first written name (or had no year), so
   *  nothing in the index accepts the names as written and a left trim may be tried. */
  nobodyAcceptsFirstName: boolean;
}

function attempt(c: Constituent, index: ReferenceIndex): Attempt {
  const exact: Scored[] = [];
  const rejections = new Map<Rejection, number>();
  const undated: string[] = [];
  for (const ref of index.refs.values()) {
    const r = score(c, ref, 0);
    if (typeof r === 'string') {
      rejections.set(r, (rejections.get(r) ?? 0) + 1);
      if (r === 'no-year') undated.push(ref.id);
    } else exact.push(r);
  }
  const count = (k: Rejection) => rejections.get(k) ?? 0;
  const nobodyAcceptsFirstName = count('year') + count('co-author') + count('suffix') + count('no-year') === 0;
  if (exact.length > 0) {
    return { resolution: decide(exact, 'more than one reference fits the evidence equally'), nobodyAcceptsFirstName };
  }

  if (!c.yearSuffix) {
    const drifted: Scored[] = [];
    for (const ref of index.refs.values()) {
      const r = score(c, ref, 1);
      if (typeof r !== 'string') drifted.push(r);
    }
    if (drifted.length === 1) return { resolution: decide(drifted, ''), nobodyAcceptsFirstName };
    if (drifted.length > 1) {
      return {
        resolution: { kind: 'ambiguous', candidates: drifted.map((d) => d.id), reason: 'year one off from more than one reference' },
        nobodyAcceptsFirstName,
      };
    }
  }

  let reason: string;
  if (nobodyAcceptsFirstName) reason = `no reference has "${c.names[0]}" as first author`;
  else if (count('year') > 0 && count('co-author') === 0 && count('suffix') === 0) reason = `no reference by "${c.names[0]}" is dated ${c.year}`;
  else if (count('no-year') > 0 && count('year') === 0 && count('co-author') === 0 && count('suffix') === 0) reason = `a reference by "${c.names[0]}" has no year`;
  else if (count('suffix') > 0 && count('co-author') === 0) reason = `no reference by "${c.names[0]}" carries the suffix ${c.year}${c.yearSuffix}`;
  else reason = `no reference lists every name written (${c.names.join(', ')}) for ${c.year}`;
  return { resolution: { kind: 'unresolved', reason, ...(undated.length > 0 ? { undated } : {}) }, nobodyAcceptsFirstName };
}

// ─── left trim ───────────────────────────────────────────────────────────

const claimedCache = new WeakMap<ReferenceIndex, Set<string>>();

/** Every normalized form any reference claims for any of its authors. */
function claimedForms(index: ReferenceIndex): Set<string> {
  let set = claimedCache.get(index);
  if (!set) {
    set = new Set();
    for (const ref of index.refs.values()) for (const form of ref.authors.keys()) set.add(form);
    claimedCache.set(index, set);
  }
  return set;
}

function isGrammarWord(w: string): boolean {
  const lower = w.toLowerCase();
  return CONNECTOR_WORDS.has(lower) || PARTICLE_WORDS.has(lower);
}

/** True when some reference claims `name` (or any of its words) as an author — then it is a
 *  real name and must never be dropped. */
function isClaimed(name: string, claimed: Set<string>): boolean {
  const ws = words(name);
  if (ws.length === 0) return false;
  if (claimed.has(joined(ws))) return true;
  return ws.some((w) => !isGrammarWord(w) && claimed.has(normalizeToken(w)));
}

/** The text between two neighbouring names, when it lies inside the text we can see. */
function separatorBefore(c: Constituent, k: number, text: string | undefined): string | null {
  const from = c.nameOffsets[k - 1] + c.names[k - 1].length;
  const to = c.nameOffsets[k];
  if (text !== undefined) return text.slice(from, to);
  if (from >= c.start && to <= c.end) return c.text.slice(from - c.start, to - c.start);
  return null;
}

function withNames(c: Constituent, names: string[], offsets: number[]): Constituent {
  return { ...c, names, nameOffsets: offsets, arity: names.length };
}

function trimmed(resolution: Resolution, c: Constituent, namesFrom: number, newStart: number): Resolution {
  if (resolution.kind === 'unresolved') return resolution;
  const trim: Trim = { namesFrom, trimmedPrefix: newStart > c.start ? c.text.slice(0, newStart - c.start) : '' };
  return { ...resolution, ...trim };
}

/**
 * Retry with leading material removed. Levels, in order: for k = 0, 1, …: the names from k on
 * (k > 0 only when name k-1 is joined to name k by a bare comma and nothing claims it), then
 * the same list with leading words of name k dropped (never starting on a connector, and only
 * when nothing claims the dropped words). The first level with any survivor decides.
 */
function leftTrim(c: Constituent, index: ReferenceIndex, text: string | undefined): Resolution | null {
  const claimed = claimedForms(index);
  for (let k = 0; k < c.names.length; k++) {
    if (k > 0) {
      const sep = separatorBefore(c, k, text);
      if (sep === null || !/^\s*,\s*$/u.test(sep) || isClaimed(c.names[k - 1], claimed)) return null;
      const a = attempt(withNames(c, c.names.slice(k), c.nameOffsets.slice(k)), index);
      if (a.resolution.kind !== 'unresolved') return trimmed(a.resolution, c, k, c.nameOffsets[k]);
      if (!a.nobodyAcceptsFirstName) return a.resolution;
    }
    // Leading words of name k.
    const name = c.names[k];
    const tokens: Array<{ word: string; at: number }> = [];
    const re = /\S+/gu;
    let m: RegExpExecArray | null;
    while ((m = re.exec(name)) !== null) tokens.push({ word: m[0], at: m.index });
    for (let j = 1; j < tokens.length; j++) {
      if (isGrammarWord(tokens[j].word) && !PARTICLE_WORDS.has(tokens[j].word.toLowerCase())) continue;
      if (tokens.slice(0, j).some((t) => !isGrammarWord(t.word) && isClaimed(t.word, claimed))) return null;
      const rest = name.slice(tokens[j].at);
      const names = [rest, ...c.names.slice(k + 1)];
      const offsets = [c.nameOffsets[k] + tokens[j].at, ...c.nameOffsets.slice(k + 1)];
      const a = attempt(withNames(c, names, offsets), index);
      if (a.resolution.kind !== 'unresolved') return trimmed(a.resolution, c, k, offsets[0]);
      if (!a.nobodyAcceptsFirstName) return a.resolution;
    }
  }
  return null;
}

/**
 * Resolve one constituent. Never guesses: a tie is reported as ambiguous, a miss as unresolved.
 * `text` — the text the constituent was found in — lets the trim see the separators between
 * names of a bare-year continuation; without it such a constituent trims only within its
 * first name.
 */
export function resolveConstituent(c: Constituent, index: ReferenceIndex, text?: string): Resolution {
  if (c.names.length === 0) return { kind: 'unresolved', reason: 'no author name could be read here' };
  if (!c.year) return { kind: 'unresolved', reason: 'no year written' };

  const base = attempt(c, index);
  if (base.resolution.kind !== 'unresolved' || !base.nobodyAcceptsFirstName) return base.resolution;
  return leftTrim(c, index, text) ?? base.resolution;
}
